import React, { useCallback, useRef, useState, useEffect } from "react";
import { cls } from "cls";
import Controls from "./Controls";
import { bounds } from "../../../utils";
import useFullScreen from "../hooks/useFullScreen";
import { Video } from "~/common/Store/models/Video";
import useGtmVideoTracking from "../hooks/useGtmVideoTracking";

interface VideoPlayerProps {
  posterImage: string;
  sources: Video["sources"];
  activePlayer: boolean;
  setActivePlayer: React.Dispatch<React.SetStateAction<boolean>>;
  currentIndex: number;
  selected: number;
  isSmallVideoPlayer: boolean;
  autoPlay?: boolean;
  title?: string;
  muted?: boolean;
  videoClasses?: ReturnType<typeof cls>;
  shouldLoadSources?: boolean;
  handleGtmVideoPlayEvent?: (sources: Video["sources"]) => void;
  useCdnForSources?: boolean;
  isProductReviewVideo?: boolean;
  shouldLoopVideo?: boolean;
  shouldAlwaysShowControls?: boolean;
  pauseVideo?: boolean;
}

function VideoPlayer({
  posterImage,
  sources,
  activePlayer,
  setActivePlayer,
  currentIndex,
  selected,
  isSmallVideoPlayer,
  autoPlay = false,
  muted = false,
  videoClasses = "",
  shouldLoadSources = true,
  handleGtmVideoPlayEvent,
  title,
  useCdnForSources = true,
  isProductReviewVideo = false,
  shouldLoopVideo = false,
  shouldAlwaysShowControls = false,
  pauseVideo = false,
}: VideoPlayerProps) {
  const [showControls, setShowControls] = useState(true);
  const [currentVideoTime, setCurrentVideoTime] = useState(0);
  const [bufferedPercentage, setBufferedPercentage] = useState(0);
  const [currentVolume, setCurrentVolume] = useState(muted ? 0 : 100);
  const [previousVolume, setPreviousVolume] = useState(100);
  const autoPlayed = useRef(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoWrapper = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const baseSourceUrl = useCdnForSources
    ? "https://cdn.webstaurantstore.com"
    : "";

  const { toggle } = useFullScreen(videoWrapper);
  const videoPlayerFullscreen = useCallback(() => {
    const videoWrapperContainer = videoWrapper.current;
    const video = videoWrapperContainer?.querySelector("video");
    if (
      videoWrapperContainer?.requestFullscreen ||
      videoWrapperContainer?.mozRequestFullscreen ||
      videoWrapperContainer?.webkitRequestFullscreen ||
      videoWrapperContainer?.msRequestFullscreen
    ) {
      toggle();
    } else {
      video?.webkitEnterFullScreen();
    }
  }, [toggle]);

  /* If hideVideoControls is called this will clear the timeout then
  runs a time out function and check whether video is playing or paused.
  If the video is playing then it will hide the controls. If video is paused it won't do anything */
  const hideVideoControls = useCallback(() => {
    if (!shouldAlwaysShowControls) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        if (videoRef.current && !videoRef.current?.paused) {
          setShowControls(false);
        }
      }, 3000);
    }
  }, [shouldAlwaysShowControls]);

  /* Shows the controls or call the hideVideoControls function depending
  on if the video is playing and if the controls are showing or not. */
  const handlePointerMove = useCallback(() => {
    if (!videoRef.current?.paused) {
      if (!showControls) {
        setShowControls(true);
      } else {
        hideVideoControls();
      }
    }
  }, [hideVideoControls, showControls]);

  const handlePointerDown = useCallback(() => {
    if (videoRef.current) {
      window.clearTimeout(timeoutRef.current);
    }
  }, []);

  const handleEnded = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      setShowControls(true);
      setActivePlayer(false);
    }
  };

  const highestSrc = shouldLoadSources
    ? sources?.high ||
      sources?.medium ||
      sources?.low ||
      sources?.product ||
      sources?.ipad
    : "";

  const { sendGtmProgressEvent, sendGtmPauseEvent, sendGtmPlayEvent } =
    useGtmVideoTracking(
      videoRef,
      `${baseSourceUrl}/videos/${highestSrc}`,
      title ?? "Unknown video title",
    );

  const handleVolumeChange = React.useCallback(
    (volume: number) => {
      if (videoRef.current) {
        if (volume === 0) {
          videoRef.current.muted = true;
        } else {
          videoRef.current.muted = false;
        }
        videoRef.current.volume = volume / 100;
        setCurrentVolume(volume);
        setPreviousVolume(previousVolume > 0 ? previousVolume : 100);
        setShowControls(true);
      }
    },
    [previousVolume],
  );

  const toggleVideo = React.useCallback(() => {
    if (
      videoRef.current &&
      !activePlayer &&
      selected === currentIndex &&
      videoRef.current.paused
    ) {
      videoRef.current.play().then(() => {
        hideVideoControls();
      });
      setActivePlayer(true);
      setShowControls(true);
      handleGtmVideoPlayEvent?.(sources);
      sendGtmPlayEvent();
      if (videoWrapper.current) {
        videoWrapper.current.focus();
      }
    } else if (videoRef.current && activePlayer) {
      videoRef.current.pause();
      sendGtmPauseEvent();
      setActivePlayer(false);
      setShowControls(true);
    }
  }, [
    activePlayer,
    currentIndex,
    handleGtmVideoPlayEvent,
    hideVideoControls,
    selected,
    sendGtmPauseEvent,
    setActivePlayer,
    sendGtmPlayEvent,
    sources,
  ]);

  function handleDownloadProgress() {
    if (!videoRef.current) return;

    if (videoRef.current) {
      const { buffered } = videoRef.current;

      if (buffered.length <= 0) return;

      const numberOfBuffers = buffered.length;
      const { duration } = videoRef.current;
      const bufferedEnd = buffered.end(numberOfBuffers - 1);
      const percentBuffered = (bufferedEnd / duration) * 100;

      setBufferedPercentage(percentBuffered);
    }
  }

  const handleTimeUpdate = (time: number) => {
    setShowControls(true);
    setCurrentVideoTime(time);
    if (videoRef.current) {
      videoRef.current.currentTime = time;
    }
  };

  const alterTime = useCallback(
    (
      e: React.SyntheticEvent | KeyboardEvent,
      keyType: string,
      timeAmount = 5,
    ) => {
      if (e.preventDefault) e.preventDefault();
      if (videoRef.current) {
        const time = keyType === "right" ? timeAmount : -timeAmount;
        const newTime = bounds(
          currentVideoTime + time,
          0,
          videoRef.current.duration,
        );

        handleTimeUpdate(newTime);
      }
    },
    [currentVideoTime],
  );

  const timeCheck = () => {
    if (!videoRef) return;

    if (videoRef.current) {
      const { currentTime } = videoRef.current;

      setCurrentVideoTime(currentTime);
    }
  };

  useEffect(() => {
    const video = videoRef.current;

    if (video) {
      video.addEventListener("progress", handleDownloadProgress);
      video.addEventListener("timeupdate", timeCheck);
    }

    return () => {
      if (video) {
        video.removeEventListener("progress", handleDownloadProgress);
        video.removeEventListener("timeupdate", timeCheck);
      }
    };
  }, []);

  useEffect(() => {
    const videoContainer = videoWrapper.current;

    if (videoContainer) {
      videoContainer.addEventListener("pointermove", handlePointerMove);
      videoContainer.addEventListener("pointerup", hideVideoControls);
      videoContainer.addEventListener("pointerdown", handlePointerDown);
    }

    return () => {
      if (videoContainer) {
        videoContainer.removeEventListener("pointermove", handlePointerMove);
        videoContainer.removeEventListener("pointerup", hideVideoControls);
        videoContainer.removeEventListener("pointerdown", handlePointerDown);
      }
    };
  }, [handlePointerDown, handlePointerMove, hideVideoControls]);

  const keyboardVideoControls = useCallback(
    (event: KeyboardEvent) => {
      switch (event.keyCode) {
        // spacebar: play/pause
        case 32:
          event.preventDefault();
          toggleVideo();
          hideVideoControls();
          break;
        // arrowLeft: go back 5 seconds
        case 37:
          event.preventDefault();
          alterTime(event, "left");
          hideVideoControls();
          break;
        // arrowUp: increase volume
        case 38:
          event.preventDefault();
          handleVolumeChange(bounds(currentVolume + 5, 0, 100));
          hideVideoControls();
          break;
        // arrowRight: go forward 5 seconds
        case 39:
          event.preventDefault();
          alterTime(event, "right");
          hideVideoControls();
          break;
        // arrowDown: decrease volume
        case 40:
          event.preventDefault();
          handleVolumeChange(bounds(currentVolume - 5, 0, 100));
          hideVideoControls();
          break;
        // f: full screen
        case 70:
          videoPlayerFullscreen();
          break;
        // m: mute volume
        case 77:
          handleVolumeChange(currentVolume > 0 ? 0 : previousVolume);
          hideVideoControls();
          break;
        default:
          break;
      }
    },
    [
      alterTime,
      currentVolume,
      handleVolumeChange,
      hideVideoControls,
      previousVolume,
      toggleVideo,
      videoPlayerFullscreen,
    ],
  );

  // keyboard controls for video
  useEffect(() => {
    const currentVideoWrapper = videoWrapper.current;
    if (currentVideoWrapper) {
      currentVideoWrapper.addEventListener("keydown", keyboardVideoControls);
    }

    return () => {
      if (currentVideoWrapper) {
        currentVideoWrapper.removeEventListener(
          "keydown",
          keyboardVideoControls,
        );
      }
    };
  }, [keyboardVideoControls]);

  // pause the video when a new item in the carousel is selected
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.pause();
      setActivePlayer(false);
    }
  }, [selected, setActivePlayer]);

  // pause the video from the parent component
  useEffect(() => {
    if (videoRef.current && pauseVideo) {
      videoRef.current.pause();
      setActivePlayer(false);
    }
  }, [pauseVideo, setActivePlayer]);

  // auto-play the video on load
  useEffect(() => {
    if (videoRef.current && autoPlay && !autoPlayed.current) {
      videoRef.current.play().then(() => {
        hideVideoControls();
      });
      setActivePlayer(true);
      handleGtmVideoPlayEvent?.(sources);
      autoPlayed.current = true;
    }
  }, [
    autoPlay,
    handleGtmVideoPlayEvent,
    hideVideoControls,
    setActivePlayer,
    sources,
  ]);

  return (
    <div
      data-testid="videoWrapper-container"
      className="relative w-full h-full"
      ref={videoWrapper}
      tabIndex={-1}
    >
      <video
        onTimeUpdate={sendGtmProgressEvent}
        preload="metadata"
        playsInline
        ref={videoRef}
        width="100%"
        className={cls("h-full", "object-contain", "w-full", videoClasses)}
        data-testid="video-container"
        onClick={toggleVideo}
        onEnded={handleEnded}
        {...(shouldLoadSources
          ? { poster: `${baseSourceUrl}${posterImage}` }
          : {})}
        muted={muted}
        loop={shouldLoopVideo}
      >
        {shouldLoadSources && (
          <>
            {sources?.high && (
              <source
                src={`${baseSourceUrl}/videos/${sources?.high}`}
                type="video/mp4"
              />
            )}
            {sources?.medium && (
              <source
                src={`${baseSourceUrl}/videos/${sources?.medium}`}
                type="video/mp4"
              />
            )}
            {sources?.low && (
              <source
                src={`${baseSourceUrl}/videos/${sources?.low}`}
                type="video/mp4"
              />
            )}
            {sources?.product && (
              <source
                src={`${baseSourceUrl}/videos/${sources?.product}`}
                type="video/mp4"
              />
            )}
            {sources?.ipad && (
              <source
                src={`${baseSourceUrl}/videos/${sources?.ipad}`}
                type="video/mp4"
              />
            )}
          </>
        )}
      </video>
      {showControls ? (
        <Controls
          alterTime={alterTime}
          toggleVideo={toggleVideo}
          activePlayer={activePlayer}
          videoRef={videoRef}
          thumbnailUrl={`${baseSourceUrl}/videos/${sources?.product}`}
          currentVideoTime={currentVideoTime}
          handleTimeUpdate={handleTimeUpdate}
          handleVolumeChange={handleVolumeChange}
          currentVolume={currentVolume}
          previousVolume={previousVolume}
          showControls={showControls}
          videoWrapper={videoWrapper}
          bufferedPercentage={bufferedPercentage}
          isSmallVideoPlayer={isSmallVideoPlayer}
          isProductReviewVideo={isProductReviewVideo}
          videoPlayerFullscreen={videoPlayerFullscreen}
        />
      ) : null}
    </div>
  );
}

export default VideoPlayer;
